/**
 * Removes an item from {@link items} that is matched by {@link predicate}.
 * If no item is found, the array reference is returned
 */
export function removeItem<TItem>(items: TItem[], predicate: (item: TItem) => boolean): TItem[] {
    const index = items.findIndex(predicate);
    if (index === -1) {
        return items;
    }
    return items.toSpliced(index, 1);
}

/**
 * Maps each item of {@link items} to a property in the resulting object by
 * using {@link keySelector}.
 * This can be used to create a strongly typed look-up object for items with a unique
 * key property, e.g. "Id".
 * @template TItem - The type of items in the array.
 * @template TKey - The type of the key property (must be number, string, or symbol).
 *
 * @example
 * // Example usage:
 * const users = [
 *     { id: 1, name: "Alice" },
 *     { id: 2, name: "Bob" }
 * ];
 *
 * const userRecord = toRecord(users, user => user.id);
 *
 * // Result:
 * // {
 * //   1: { id: 1, name: "Alice" },
 * //   2: { id: 2, name: "Bob" }
 * // }
 */
export function toRecord<TItem, TKey extends number | string | symbol>(
    items: TItem[],
    keySelector: (item: TItem) => TKey
): Record<TKey, TItem> {
    return items.reduce(
        (acc, item) => {
            const key = keySelector(item);
            acc[key] = item;
            return acc;
        },
        {} as Record<TKey, TItem>
    );
}

/**
 * Returns a new array with unique objects based on the specified key.
 *
 * @template T - The type of the objects in the array.
 * @param {T[]} array - The array of objects to filter.
 * @param {keyof T} key - The key to determine uniqueness.
 * @returns {T[]} A new array containing unique objects based on the given key.
 */
export function uniqueByKey<T>(array: T[], key: keyof T) {
    return [...new Map(array.map(item => [item[key], item])).values()];
}

/**
 * Compares two arrays to determine if they contain the same elements,
 * regardless of order and including duplicate counts.
 *
 * @template T
 * @param {T[]} arr1 - The first array to compare.
 * @param {T[]} arr2 - The second array to compare.
 * @returns {boolean} True if arrays contain the same elements with the same frequencies, false otherwise.
 */
export function arraysEqualUnordered<T>(arr1: T[], arr2: T[]): boolean {
    if (arr1.length !== arr2.length) return false;

    /**
     * Builds a frequency map of array elements.
     * @param {T[]} arr - Input array.
     * @returns {Map<T, number>} A map of element counts.
     */
    const countMap = (arr: T[]): Map<T, number> => {
        const map = new Map<T, number>();
        for (const val of arr) {
            map.set(val, (map.get(val) ?? 0) + 1);
        }
        return map;
    };

    const map1 = countMap(arr1);
    const map2 = countMap(arr2);

    if (map1.size !== map2.size) return false;

    for (const [key, count] of map1) {
        if (map2.get(key) !== count) return false;
    }

    return true;
}

/**
 * Type safe helper to filter out non-truthy values-.
 *
 * @example
 * // Example usage:
 * type User = {
 *  id: number;
 *  name: string;
 * }
 * const users: <User | null>[] = [
 *     null,
 *     { id: 2, name: "Bob" }
 * ];
 *
 * const filteredUsers = users.filter(isTruthy);
 * // filteredUser is of type User[]
 * const user = filteredUsers.find(user => user.id === 2);
 */
export function isTruthy<T>(value: T): value is NonNullable<T> {
    return Boolean(value);
}
